import React, { useMemo } from "react";
import { Box, Typography, Chip, Tooltip } from "@mui/material";
import ReusableTable from "../../../../../components/ReusableTable/ReusableTable";
import { getRoleBadgeColor } from "../utils/styleHelpers";

const AccessMatrixTable = ({ filteredUsers, uniqueSystems, loading }) => {
  const columns = useMemo(() => {
    const cols = [
      {
        label: "User",
        field: "user",
        align: "left",
        width: "200px",
        minWidth: 200,
      },
    ];

    uniqueSystems.forEach((system) => {
      cols.push({
        label: system.displayLabel || system.label,
        field: `system_${system.id}`, 
        align: "center",
        width: "150px",
        minWidth: 150,
        systemData: system,
      });
    });

    return cols;
  }, [uniqueSystems]);

  // Render cell content
  const renderCell = (row, column) => {
    // User column
    if (column.field === "user") {
      return (
        <Box>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.name || "Unknown User"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: "0.7rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
            }}
          >
            {row.orgRole}
          </Typography>
          {row.email && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: "0.7rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              {row.email}
            </Typography>
          )}
        </Box>
      );
    }

    // System access columns - extract system key from field name
    const systemKey = column.field.replace("system_", "");
    const access = row.systemAccess[systemKey];
    const system = column.systemData;

    if (!access) {
      return <Box sx={{ color: "#b3b3b3" }}>×</Box>;
    }

    const badgeColors = getRoleBadgeColor(access.role);

    return (
      <Tooltip
        title={
          <Box>
            <Typography variant="caption" fontWeight={600}>
              {system.displayLabel || system.label}
            </Typography>
            {system.accountLabel && (
              <>
                <br />
                <Typography variant="caption">Account: {system.accountLabel}</Typography>
              </>
            )}
            <br />
            <Typography variant="caption">Role: {access.role}</Typography>
          </Box>
        }
        arrow
      >
        <Chip
          label={access.role}
          size="small"
          sx={{
            bgcolor: badgeColors.bg,
            color: badgeColors.color,
            border: `1px solid ${badgeColors.border}`,
            fontWeight: 500,
            fontSize: "0.7rem",
            height: 22,
          }}
        />
      </Tooltip>
    );
  };

  // Render header cell with subtitle
  const renderHeaderCell = (column) => {
    if (column.field === "user") {
      return (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            User
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.7rem",
              fontWeight: 400,
              color: "text.secondary",
            }}
          >
            Role
          </Typography>
        </Box>
      );
    }

    // System columns with tooltip
    const system = column.systemData;
    return (
      <Tooltip
        title={
          <Box>
            <Typography variant="caption" fontWeight={600}>
              {system.displayLabel || system.label}
            </Typography>
            {system.accountLabel && (
              <>
                <br />
                <Typography variant="caption">Account: {system.accountLabel}</Typography>
              </>
            )}
            <br />
            <Typography variant="caption">
              Category: {system.category}
            </Typography>
          </Box>
        }
        arrow
      >
        <Box>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {system.displayLabel || system.label}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.7rem" }}
          >
            {system.category}
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box sx={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
      <Box sx={{ minWidth: "max-content" }}>
        <ReusableTable
          columns={columns}
          data={filteredUsers}
          renderCell={renderCell}
          renderHeaderCell={renderHeaderCell}
          showPagination={false}
          loading={loading}
          height="600px"
          enableHorizontalScroll
        />
      </Box>
    </Box>
  );
};

export default AccessMatrixTable;

